/**
 * LegalPage - Renders the Terms of Service or Privacy Policy.
 *
 * These are interim placeholder documents so the links referenced from the
 * Login and Register pages resolve to real, reviewable content (issue #100).
 * The legal text is intentionally plain and conservative; replace with the
 * finalised legal copy when available.
 *
 * @param {('terms'|'privacy')} doc - Which document to render.
 */
import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Link, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';

const LAST_UPDATED = '17 May 2026';

const CONTENT = {
  terms: {
    title: 'Terms of Service',
    icon: <GavelIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />,
    sections: [
      {
        heading: '1. About ParlSearch',
        body:
          'ParlSearch is a free, independent tool for searching and exploring ' +
          'public Australian parliamentary data. It is not affiliated with, or ' +
          'endorsed by, the Parliament of Australia or any government agency. ' +
          'All source data is published by the Parliament and other public bodies.'
      },
      {
        heading: '2. Acceptable use',
        body:
          'You may use ParlSearch for lawful research, journalistic, civic and ' +
          'educational purposes. You must not attempt to disrupt the service, ' +
          'circumvent access controls, or use automated means to place an ' +
          'unreasonable load on the service.'
      },
      {
        heading: '3. Accounts',
        body:
          'An account is optional and only enables personalised features such ' +
          'as saved searches and email alerts. You are responsible for keeping ' +
          'your credentials secure. You may delete your account at any time ' +
          'from Settings.'
      },
      {
        heading: '4. Accuracy & no warranty',
        body:
          'Data is provided "as is" without warranty of any kind. While we aim ' +
          'to reflect the official record faithfully, ParlSearch may contain ' +
          'errors, omissions or out-of-date information. Always confirm ' +
          'critical facts against the official Parliament of Australia sources.'
      },
      {
        heading: '5. Changes',
        body:
          'These terms may be updated from time to time. Continued use of the ' +
          'service after a change constitutes acceptance of the revised terms.'
      },
      {
        heading: '6. Contact',
        bodyEl: (
          <>
            Questions about these terms can be sent to{' '}
            <Link href="mailto:support@parlsearch.cc">support@parlsearch.cc</Link>.
          </>
        )
      }
    ]
  },
  privacy: {
    title: 'Privacy Policy',
    icon: <PrivacyTipIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />,
    sections: [
      {
        heading: '1. What we collect',
        body:
          'If you create an account we store the email address and display ' +
          'name you provide, a securely hashed password, and the saved ' +
          'searches, alerts and preferences you choose to create. Browsing ' +
          'ParlSearch without an account does not require any personal data.'
      },
      {
        heading: '2. How we use it',
        body:
          'Account data is used solely to provide the features you opt into ' +
          '(saved searches, email alerts and personalised settings). We do not ' +
          'sell personal data and we do not use it for advertising.'
      },
      {
        heading: '3. Analytics',
        body:
          'We use privacy-respecting, aggregate usage analytics to understand ' +
          'which features are useful. These analytics do not identify ' +
          'individual users.'
      },
      {
        heading: '4. Email',
        body:
          'If you enable email alerts we use your email address only to send ' +
          'the alerts you requested. You can disable alerts at any time from ' +
          'Settings.'
      },
      {
        heading: '5. Data retention & deletion',
        body:
          'Account data is retained until you delete your account, after which ' +
          'it is removed from active systems. You may request deletion at any ' +
          'time.'
      },
      {
        heading: '6. Contact',
        bodyEl: (
          <>
            Privacy questions or deletion requests can be sent to{' '}
            <Link href="mailto:support@parlsearch.cc">support@parlsearch.cc</Link>.
          </>
        )
      }
    ]
  }
};

function LegalPage({ doc = 'terms' }) {
  const navigate = useNavigate();
  const content = CONTENT[doc] || CONTENT.terms;

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', px: 2, py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {content.icon}
          <Typography component="h1" variant="h4">
            {content.title}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last updated: {LAST_UPDATED}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This is an interim summary document. It is provided so you can review
          the basis on which ParlSearch is offered before creating an account.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {content.sections.map((s) => (
          <Box key={s.heading} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {s.heading}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {s.bodyEl || s.body}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary">
          See also our{' '}
          {doc === 'terms' ? (
            <Link component={RouterLink} to="/privacy" underline="hover">
              Privacy Policy
            </Link>
          ) : (
            <Link component={RouterLink} to="/terms" underline="hover">
              Terms of Service
            </Link>
          )}
          .
        </Typography>
      </Paper>
    </Box>
  );
}

export default LegalPage;
